<template>
  <div class="oring-stepper">
    <Steps
      :steps="possibleSteps"
      :current-step="oringDesignSpaceCurrentStep"
      :completed-steps="oringDesignSpaceCompletedSteps"
      @go-to-step="goToStep($event)"
    />
    <div class="step-content">
      <div class="step-content__subheading">
        <h2>{{ currentStepTitle }}</h2>
        <ToggleSwitchSquare
          v-show="currentStepObject?.showToggleSwitch"
          :is-checked="oringCalculatorImperial"
          :label-right="i18n.tv('inch', 'ORingConfigurator')"
          :label-left="i18n.tv('mm', 'ORingConfigurator')"
          @change-value="performUnitLengthSwitch($event)"
        />
      </div>
      <component
        ref="stepComponentRef"
        :is="currentStepComponent"
      />
      <div class="step-content__buttons">
        <div class="left-buttons">
          <ReactiveButton
            v-if="oringDesignSpaceCurrentStep !== 1"
            @click="goToPreviousStep"
          >
            {{ i18n.tv('Frontend.Wizard.PreviousButtonText', 'ORingConfiguratorImport') }}
          </ReactiveButton>
        </div>
        <div class="right-buttons">
          <ReactiveButton
            v-if="oringDesignSpaceCurrentStep !== possibleSteps.length"
            @click="goToNextStep"
            :disabled="!isCurrentStepValid"
            :is-primary-button="false"
            type="submit"
            :button-text="i18n.tv('Frontend.Wizard.NextButtonText', 'ORingConfiguratorImport')"
            :is-loading="isLoading"
          />
          <ReactiveButton
            v-if="oringDesignSpaceCurrentStep===possibleSteps.length"
            @click="showStartPopup= true"
            :is-primary-button="true"
            :button-text="i18n.tv('startNewSearch', 'ORingConfigurator')"
          />
        </div>
      </div>
    </div>
    <LeaveConfirmationPopup
      :show="showStartPopup"
      :size="POPUP.Size.small"
      :title="i18n.tv('startNewSearch', 'ORingConfigurator')"
      :content="i18n.tv('startNewSearchContent', 'ORingConfigurator')"
      :stay-button-text="i18n.tv('Frontend.Button.Cancel', 'ORingConfiguratorImport')"
      :leave-button-text="i18n.tv('startNewSearch', 'ORingConfigurator')"
      @update:show="showStartPopup = $event"
      @leave="startNewSearch"
    />
  </div>
</template>

<script setup lang="ts">
import {ref, computed} from 'vue'
import useI18nExtended from '~/composables/usei18nExtended'
import Steps from '~/components/input-components/Steps.vue'
import {Step} from '~/types/index.d.ts'
import {POPUP} from '~/assets/js/constants'
import ORingAssistantStep1 from '~/components/oring-calculator/ORingAssistantStep1.vue'
import ORingAssistantStep2 from '~/components/oring-calculator/ORingAssistantStep2.vue'
import {useORingCalculatorStore} from '@/store/oring-calculator'
import {useOringCalculatorService} from '~/composables/oring_calc_service'
import LeaveConfirmationPopup from '~/components/popup/LeaveConfirmationPopup.vue'
import ORingDesignSpaceAssistantStep4 from '~/components/oring-calculator/ORingDesignSpaceAssistantStep4.vue'
import ORingDesignSpaceAssistantStep5 from '~/components/oring-calculator/ORingDesignSpaceAssistantStep5.vue'
import ORingAssistantStep3 from '~/components/oring-calculator/ORingAssistantStep3.vue'
import {storeToRefs} from 'pinia'
import ToggleSwitchSquare from '~/components/input-components/ToggleSwitchSquare.vue'
import {useOringScrollToTop} from '~/composables/oring/useOringScrollToTop'

const showStartPopup = ref(false)

const i18n = useI18nExtended()
const store = useORingCalculatorStore()
const {scrollToTop} = useOringScrollToTop()

const {oringDesignSpaceCalculateHousingRequest, performUnitLengthSwitch} = store
const {
  oringDesignSpaceCurrentHousing,
  oringCalculatorImperial,
  oringDesignSpaceCurrentStep,
  oringDesignSpaceCompletedSteps
} = storeToRefs(store)
const {calculateHousingDesignAssistant} = useOringCalculatorService()

const steps: Step[] = [
  {number: 1, title: i18n.tv('sealingCompressionAndMediaSelection', 'ORingConfigurator'), component: ORingAssistantStep1},
  {number: 2, title: i18n.tv('movementAndGroove', 'ORingConfigurator'), component: ORingAssistantStep2},
  {number: 3, title: i18n.tv('mountingPosition', 'ORingConfigurator'), component: ORingAssistantStep3},
  {number: 4, title: i18n.tv('Frontend.Title.Dimensions', 'ORingConfiguratorImport'), component: ORingDesignSpaceAssistantStep4, showToggleSwitch: true},
  {number: 5, title: i18n.tv('result', 'ORingConfigurator'), component: ORingDesignSpaceAssistantStep5, showToggleSwitch: true}
]

const isLoading = ref(false)

const possibleSteps = computed(() => {
  let adjustedNumber = 1
  return steps
    .filter(step => (step.number !== 2 && step.number !== 3)
      || (step.number === 2 && store.oringAssistantIsRadial)
      || (step.number === 3 && store.oringAssistantIsRadial && !store.oringAssistantGrooveInShaft)
    )
    .map(step => ({...step, number: adjustedNumber++}))
})

const stepComponentRef: any = ref(null)

const currentStepTitle = computed(() => {
  return possibleSteps.value.find(step => step.number === oringDesignSpaceCurrentStep.value)?.title || ''
})
const currentStepComponent = computed(() => {
  return possibleSteps.value.find(step => step.number === oringDesignSpaceCurrentStep.value)?.component || null
})
const currentStepObject = computed(() => {
  return possibleSteps.value.find(step => step.number === oringDesignSpaceCurrentStep.value)
})
const isCurrentStepValid = computed( () => {
  if (stepComponentRef.value?.isValid !== undefined) {
    return stepComponentRef.value.isValid
  }
  return true
})

const goToStep = async (number: number) => {
  oringDesignSpaceCompletedSteps.value = steps
    .filter(step => step.number < number)
    .map(step => step.number)
  oringDesignSpaceCurrentStep.value = number
  scrollToTop('auto')
}

const goToNextStep = async () => {
  if (oringDesignSpaceCurrentStep.value === possibleSteps.value.length - 1) {
    isLoading.value = true
    oringDesignSpaceCurrentHousing.value = await calculateHousingDesignAssistant(oringDesignSpaceCalculateHousingRequest())
    isLoading.value = false
  }

  if (!oringDesignSpaceCompletedSteps.value.includes(oringDesignSpaceCurrentStep.value)) {
    oringDesignSpaceCompletedSteps.value.push(oringDesignSpaceCurrentStep.value)
  }

  oringDesignSpaceCurrentStep.value += 1
  scrollToTop('auto')
}

const goToPreviousStep = () => {
  if (oringDesignSpaceCurrentStep.value > 1) {
    oringDesignSpaceCurrentStep.value -= 1
    scrollToTop('auto')
  }
}

const startNewSearch = () => {
  showStartPopup.value = false
  store.resetDesignSpaceStates()
  scrollToTop('auto')
}

</script>

<style lang="scss">
.oring-stepper {
  .step-content {
    h2 {
      margin: 0 0 .5rem;
    }

    &__subheading {
      display: flex;
      justify-content: space-between;
      align-items: center;

      @include breakpoint-down($md) {
        flex-direction: column;
        align-items: flex-start;

        // place the toggle below the heading with same spacing as other parts
        .toggle-switch-square {
          margin-top: 1.5rem;
        }
      }
    }

    &__buttons {
      display: flex;
      justify-content: space-between;

      .left-buttons {
        display: flex;
        flex: 1;

        .k-button.k-button-solid-primary {
          background-color: $color__fst__blue__010;
          color: $color__fst__primary;
        }
      }

      .right-buttons {
        display: flex;
        justify-content: flex-end;
        flex: 1;
      }
    }
  }
}
</style>
